import { useIdentity } from '@vp/auth-react';
import { useContext, useState, useRef, useCallback, useEffect } from 'react';
import { ChatbotContext } from './src-contexts-ChatbotContext.js';
import { transformChatbotResponse, showAgentButton } from './src-utils-chatbot-order-tracking-response-handlers.js';
import { handleTextMessage, handleMessageWithDifferentQuery, handleProductSelect, handleOrderSelect } from './src-utils-chatbot-order-tracking-request-handlers.js';
import { useCustomTracking } from './src-hooks-custom-hooks-useTracking.js';
import { API_ENDPOINTS } from './src-config-api-config.js';
import { getChatbotToken, getRestId, setChatbotToken } from './src-utils-session-storage-utils.js';
import { useNRLogging } from './src-hooks-custom-hooks-useNRLogging.js';
import { CAContext } from './src-contexts-CAContext.js';
import { util } from './src-utils-miaw-common.js';
import { getFlowInfo } from './src-utils-chatbot-order-tracking-order-flow-utils.js';
import { getChatbotSessionInfo } from './src-utils-chatbot-api-utils.js';

const useCBChatMessage = () => {
  const { logCustomAttributesNR } = useNRLogging();
  const {
    chatState: { locale, availableSchedule, gcpChatData },
    setChatState
  } = useContext(CAContext);
  const { setState: setChatbotState } = useContext(ChatbotContext);
  const { sendTrackingEvent } = useCustomTracking();
  const identity = useIdentity();
  const [data, setData] = useState({ messages: [], flowType: void 0 });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState();
  const [requestData, setRequestData] = useState();
  const [query, setQuery] = useState();
  const abortControllerRef = useRef(null);
  const cancel = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
      setLoading(false);
    }
  }, [setChatbotState]);
  const trigger = useCallback(
    (msgData) => {
      cancel();
      sendTrackingEvent("Chatbot Clicked", "Chatbot:Message", "chatUserMessage");
      switch (msgData.type) {
        case "order_select": {
          const { query: orderQuery, userMessage: orderMessage } = handleOrderSelect(msgData);
          setQuery(orderQuery);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message) => {
              if (message.customMessageType === "orderListMessage") {
                return { ...message, additionalComponents: void 0 };
              }
              return message;
            });
            return {
              ...prev,
              conversationMessages: [...updateConversationMessages, orderMessage],
              orderNumber: msgData.messageData.orderNumber
            };
          });
          break;
        }
        case "product_select": {
          const { query: productQuery, userMessage: productMessage, selectedProductIds } = handleProductSelect(msgData);
          setQuery(productQuery);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message) => {
              if (message.customMessageType === "productListMessage") {
                return { ...message, additionalComponents: void 0 };
              }
              return message;
            });
            return { ...prev, conversationMessages: [...updateConversationMessages, productMessage], selectedProductIds };
          });
          break;
        }
        case "non_logged_in_order_tracking": {
          setQuery(msgData.messageData?.query);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message, index) => {
              if (index === prev.conversationMessages.length - 1) {
                return { ...message, additionalComponents: void 0 };
              }
              return message;
            });
            return {
              ...prev,
              conversationMessages: [
                ...updateConversationMessages,
                {
                  id: util.generateUUID(),
                  messageContent: msgData.messageData.messageText,
                  type: "user",
                  timestamp: (/* @__PURE__ */ new Date()).getTime(),
                  isMessageRead: false,
                  showFeedback: false
                }
              ]
            };
          });
          break;
        }
        case "text_message_with_different_query": {
          const { query: textQuery, userMessage: textMessage } = handleMessageWithDifferentQuery(msgData);
          setQuery(textQuery);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message, index) => {
              if (index === prev.conversationMessages.length - 1) {
                return { ...message, additionalComponents: void 0 };
              }
              return message;
            });
            return { ...prev, conversationMessages: [...updateConversationMessages, textMessage] };
          });
          break;
        }
        case "text_message":
        default: {
          const { query: textQuery, userMessage: textMessage } = handleTextMessage(msgData);
          setQuery(textQuery);
          setChatbotState((prev) => {
            const updateConversationMessages = prev.conversationMessages.map((message, index) => {
              if (index === prev.conversationMessages.length - 1) {
                return { ...message, additionalComponents: void 0 };
              }
              return message;
            });
            return { ...prev, conversationMessages: [...updateConversationMessages, textMessage] };
          });
          break;
        }
      }
      setRequestData(msgData);
    },
    [cancel, sendTrackingEvent, setChatbotState]
  );
  useEffect(() => {
    const fetchData = async () => {
      if (requestData) {
        setLoading(true);
        setChatbotState((prev) => ({
          ...prev,
          showHelperButton: false
        }));
        abortControllerRef.current = new AbortController();
        const signal = abortControllerRef.current.signal;
        try {
          const chatbotToken = getChatbotToken();
          const { authorizationHeader, canonicalId, isSignedIn } = identity;
          const myHeaders = new Headers();
          myHeaders.append("Authorization", authorizationHeader || "");
          myHeaders.append("Content-Type", "application/json");
          myHeaders.append("Chatbot-Authorization", `Bearer ${chatbotToken}`);
          const requestBody = {
            query,
            shopper_id: canonicalId || "",
            request_id: getRestId() || "",
            chatLocale: locale,
            agentChatInfo: {
              isChatEnabled: gcpChatData?.status.isChatEnabled,
              nextAvailableSchedule: availableSchedule
            },
            guest_user: isSignedIn ? "false" : "true"
          };
          const guestUserEmail = requestData.type === "non_logged_in_order_tracking" && requestData.messageData.email || void 0;
          if (guestUserEmail) {
            requestBody.guest_user_email = guestUserEmail;
          }
          const requestOptions = {
            method: "POST",
            headers: myHeaders,
            body: JSON.stringify(requestBody),
            redirect: "follow",
            signal
            // Add the AbortController signal to the request
          };
          logCustomAttributesNR("ca-chatbot-api-request", {
            logLevel: "info",
            message: "Sending message to chatbot",
            type: requestData.type,
            apiType: "sendChatbotMessage"
          });
          fetch(`${API_ENDPOINTS.CHATBOT_BASE_URL()}/chat`, requestOptions).then((response) => {
            if (!response.ok) {
              if (response.status === 401) {
                setChatbotState((prev) => ({ ...prev, isSessionExpired: true }));
                setChatState((prev) => ({
                  ...prev,
                  endChatInfo: {
                    sessionType: "chatbot",
                    sessionId: getChatbotSessionInfo(chatbotToken || void 0)?.sessionId || ""
                  }
                }));
              }
              const error2 = new Error(`API responded with status: ${response.status}`);
              error2.name = "ApiError";
              throw error2;
            }
            return response.json();
          }).then((result) => {
            logCustomAttributesNR("ca-chatbot-api-response", {
              logLevel: "info",
              message: "Ending chatbot session API success",
              apiType: "sendChatbotMessage"
            });
            const cbResult = result;
            const msg = transformChatbotResponse(cbResult, getRestId(false));
            let showButton = false;
            msg.forEach((message) => {
              const msgContent = message.messageContent;
              if (Array.isArray(msgContent)) {
                msgContent.forEach((content) => {
                  showButton = showAgentButton(content);
                });
              }
            });
            const flowInfo = getFlowInfo(cbResult?.response?.data?.flow_name || "", showButton);
            if (cbResult?.refreshed_token) {
              setChatbotToken(cbResult.refreshed_token, isSignedIn);
            }
            setData({ messages: msg, flowType: flowInfo });
            setError(void 0);
          }).catch((error2) => {
            if (error2.name !== "AbortError") {
              setError(error2);
              console.error(error2);
              logCustomAttributesNR("ca-chatbot-api-error", {
                errorName: error2.name,
                errorStack: error2.stack,
                message: error2.message,
                logLevel: "error",
                apiType: "sendChatbotMessage"
              });
            }
          }).finally(() => {
            if (!signal.aborted) {
              setLoading(false);
            }
            setRequestData(void 0);
          });
        } catch (err) {
          if (err && err.name !== "AbortError") {
            setError(err);
          }
          console.error(err);
          logCustomAttributesNR("ca-chatbot-api-error", {
            errorName: err.name,
            errorStack: err.stack,
            message: err.message,
            logLevel: "error",
            apiType: "sendChatbotMessage"
          });
        }
      }
    };
    fetchData();
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [requestData, gcpChatData, availableSchedule, locale]);
  return {
    data,
    loading,
    error,
    trigger,
    cancel
    // Expose the cancel function in the hook's return value
  };
};

export { useCBChatMessage };
//# sourceMappingURL=src-hooks-chatbot-useCBChatMessage.js.map
